import { useToggle } from "@uidotdev/usehooks";
import { type PropsWithChildren, useContext, useMemo, useState } from "react";

import {
  type EditorContextType as CoreEditorContextType,
  createEditorContext,
  type TipTapEditor,
} from "../core";
import "./_styles/index.css";

/**
 * Rich-text editor context type
 * Extends core EditorContextType with inline menu state
 */
type RichTextEditorContextType = CoreEditorContextType & {
  openInlineMenu: boolean;
  toggleInlineMenu: (open?: boolean) => void;
};

type RichTextEditorProviderProps = PropsWithChildren<{
  openInlineMenu?: boolean;
}>;

/**
 * Rich-text editor context
 * Extends core editor context with inline menu functionality
 */
const RichTextEditorContext = createEditorContext<RichTextEditorContextType>();

/**
 * Rich-text EditorProvider
 * Provides editor state + inline menu toggle for rich-text editing
 */
export function Editor({
  children,
  openInlineMenu: openInlineMenuProps = false,
}: RichTextEditorProviderProps) {
  const [openInlineMenu, toggleInlineMenu] = useToggle(openInlineMenuProps);
  const [editor, setEditor] = useState<TipTapEditor | null>(null);

  const value = useMemo(
    () => ({
      editor,
      setEditor,
      openInlineMenu,
      toggleInlineMenu,
    }),
    [editor, openInlineMenu, toggleInlineMenu],
  );

  return (
    <RichTextEditorContext.Provider value={value}>
      {children}
    </RichTextEditorContext.Provider>
  );
}

/**
 * Hook to access rich-text editor context
 */
export function useEditorContext(
  componentName?: string,
): RichTextEditorContextType {
  const context = useContext(RichTextEditorContext);
  if (!context) {
    const name = componentName || "Component";
    throw new Error(`${name} must be used within a rich-text Editor provider`);
  }
  return context;
}

export type { RichTextEditorContextType };
